import { FC, useRef } from 'react';
import { styled, Box, Button, CircularProgress } from '@mui/material';
import { PhotoOutlined } from '@mui/icons-material';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormValues } from '../../types';
import { UploadType } from 'further-types/files';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { Column, Row } from 'further-ui/components/Layout';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';

const StyledColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
  justifyContent: 'flex-start',
  alignItems: isCompact ? 'center' : 'flex-start',
  flexDirection: isCompact ? 'row' : 'column',
}));

export const LogoPreviewBoxLogo = styled('img', {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'isCompact',
})<{ variant: 'light' | 'dark'; isCompact?: boolean }>(
  ({ theme, variant, isCompact }) => ({
    maxHeight: isCompact ? 30 : 100,
    maxWidth: '90%',
    border: isCompact
      ? undefined
      : `1px dashed ${theme.palette.grey[variant === 'light' ? 400 : 700]}`,
  }),
);

export const LogoPreviewBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'variant' && prop !== 'isCompact' && prop !== 'customBgColor',
})<{ variant: 'light' | 'dark'; isCompact?: boolean; customBgColor?: string }>(
  ({ theme, variant, isCompact, customBgColor }) => ({
    width: isCompact ? 40 : '100%',
    cursor: 'pointer',
    minHeight: isCompact ? 40 : 200,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: customBgColor?.trim()
      ? customBgColor
      : variant === 'light'
      ? theme.palette.grey[200]
      : theme.palette.grey[800],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
  }),
);

type Props = {
  name: 'lightMainLogo' | 'darkMainLogo' | 'squareLogo' | 'reportCoverLogo';
  boxVariant: 'light' | 'dark';
  control: Control<FormValues>;
  isCompact?: boolean;
  backgroundColor?: string;
  allowRemove?: boolean;
};

const LogoUploadBox: FC<Props> = ({
  name,
  boxVariant,
  control,
  isCompact = false,
  backgroundColor,
  allowRemove = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const notification = useNotification();
  const { loading: isUploading, uploadFile } = useFileUpload();

  const acceptedFileTypes = getValidFileTypesForUploadType(
    UploadType.FirmLogos,
  ).join(',');

  const logoPreview = useWatch({
    control,
    name,
  })?.previewUri;

  const handleLogoUpload = (
    onChange: (value: { previewUri: string; objectKey: string }) => void,
    file?: File,
  ) => {
    if (!file) return;

    const maxSize = getMaxSizeForUploadType(UploadType.FirmLogos, 'kb');
    if (file.size / 1000 > maxSize) {
      notification.error(`File size must be less than ${maxSize}KB`);
      return;
    }

    onChange({
      previewUri: URL.createObjectURL(file),
      objectKey: '',
    });

    const handleFileUpload = async (file: File) => {
      const { data, error } = await uploadFile(file, UploadType.FirmLogos);
      if (error) {
        notification.error(error.message);
        return;
      }
      if (!data) return;

      onChange({
        previewUri: data.url,
        objectKey: data.filePath,
      });
    };

    void handleFileUpload(file);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={acceptedFileTypes}
            hidden
            onChange={(event) =>
              handleLogoUpload(onChange, event.target.files?.[0])
            }
          />
          <StyledColumn spacing="sm" isCompact={isCompact}>
            <LogoPreviewBox
              variant={boxVariant}
              isCompact={isCompact}
              customBgColor={backgroundColor}
              onClick={() => inputRef.current?.click()}
            >
              {isUploading ? (
                <CircularProgress size={24} color="info" />
              ) : logoPreview ? (
                <LogoPreviewBoxLogo
                  variant={boxVariant}
                  src={logoPreview}
                  isCompact={isCompact}
                />
              ) : (
                <PhotoOutlined fontSize={isCompact ? 'medium' : 'large'} />
              )}
            </LogoPreviewBox>
            <Row justify="space-between" centered fullWidth>
              <Button
                size="large"
                variant="contained"
                disabled={isUploading}
                color="primary"
                onClick={() => inputRef.current?.click()}
              >
                Upload logo
              </Button>
              {allowRemove && logoPreview && (
                <Button
                  size="large"
                  variant="outlined"
                  disabled={isUploading}
                  onClick={() => {
                    onChange(undefined);
                    if (inputRef.current) inputRef.current.value = '';
                  }}
                >
                  Remove logo
                </Button>
              )}
            </Row>
          </StyledColumn>
        </>
      )}
    />
  );
};

export default LogoUploadBox;
